import { Container } from 'components'
import DMBaseButton from 'components/modern/DMBaseButton'
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown'
import { useGetLocationPartners, useGetLocations, useConnectionStatuses } from 'hooks'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Location } from 'types'
import { getConnectionsURL } from 'utils'

import DigitalMarketing from './DigitalMarketing/DigitalMarketing'
import POS from './POS/POS'
import TOOS from './TOOS/TOOS'
import Platforms from './Platforms/Platforms'
import * as Styled from './styles'

const Connections = () => {
  const { defaultLocation, setDefaultLocation, findAndSetDefaultLocation } = useGetLocations()
  const navigate = useNavigate()
  const [isRefreshingAll, setIsRefreshingAll] = useState(false)

  const { locationId: urlLocationId } = useParams()
  const locationId = defaultLocation !== null ? String(defaultLocation?.id) : ''

  const { partner, platforms, isLoading, refetch, organization } =
    useGetLocationPartners(locationId)

  // Use shared connection statuses hook for both POS and TOOS
  const connectionStatuses = useConnectionStatuses(partner || {} as any)

  // set location id from params
  useEffect(() => {
    if (urlLocationId && locationId && locationId !== urlLocationId) {
      findAndSetDefaultLocation(urlLocationId, getConnectionsURL)
    }
  }, [urlLocationId, locationId])

  // if the page is loaded without an id in the url param, set the default location as the id.
  useEffect(() => {
    if (defaultLocation && !urlLocationId) {
      navigate(getConnectionsURL(defaultLocation.id))
    }
  }, [defaultLocation])

  // Handle location change from the dropdown
  const handleLocationChange = (location: Location) => {
    setDefaultLocation({
      id: location.id,
      name: location.name,
      organization_id: location.organization_id,
      organization_name: location.organization_name,
      timezone: location.timezone,
    })

    // Update the locationId on URL
    navigate(getConnectionsURL(location?.id))
  }

  // Handle refresh all button click
  const handleRefreshAll = async () => {
    if (isRefreshingAll || connectionStatuses.isRefreshing) return
    
    setIsRefreshingAll(true)
    try {
      await connectionStatuses.refetchAll()
    } finally {
      setIsRefreshingAll(false)
    }
  }

  return (
    <Container>
      <Styled.ConnectionsHeader>
        <Styled.LocationDropdownContainer>
          <SingleLocationDropdown onLocationChange={handleLocationChange} />
        </Styled.LocationDropdownContainer>
        <DMBaseButton
          variant="outlined"
          size="small"
          onClick={handleRefreshAll}
          disabled={isRefreshingAll || !!connectionStatuses.isRefreshing || !partner}
        >
          {isRefreshingAll ? 'Updating...' : 'Refresh All'}
        </DMBaseButton>
      </Styled.ConnectionsHeader>
      {partner && (
        <POS
          partner={partner}
          isLoading={isLoading || connectionStatuses.isLoading}
          connectionStatuses={connectionStatuses.posConnections}
          onRefresh={connectionStatuses.refreshStatus}
          isRefreshing={connectionStatuses.isRefreshing}
        />
      )}
      {partner && (
        <TOOS
          partner={partner}
          isLoading={isLoading || connectionStatuses.isLoading}
          connectionStatuses={connectionStatuses.toosConnections}
          onRefresh={connectionStatuses.refreshStatus}
          isRefreshing={connectionStatuses.isRefreshing}
        />
      )}
      {partner && (
        <Platforms
          partner={partner}
          locationPlatforms={platforms}
          isLoading={isLoading}
          refetchLocationPartners={refetch}
        />
      )}
      {partner && (
        <DigitalMarketing
          location={partner}
          locationName={defaultLocation?.name || ''}
          organization={organization}
          isLoading={isLoading}
          sectionTitle='Digital Marketing'
        />
      )}
    </Container>
  )
}

export default Connections
